/**
 * wayai access explain — the access set a hub agent's toolset calls send, and which layer removed each
 * value (#5358 PR 15).
 *
 * The same `/api/setup/hubs/:id/agents/:agentId/access-explain` route the app uses: read-only, `hub:read`
 * on the hub, and an org-admin grant to ask of anyone but yourself. The set is worked out server-side by
 * the same functions a toolset call's is. A refusal's message is the route's, sanitized.
 */

import { parseArgs } from 'node:util';
import { getAccessToken, requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { extractApiMessage } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { UUID_RE, wantsHelp } from '../lib/utils.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { USER_ID_REGEX, type AccessAssignment, type AccessExplain, type AccessLayer } from '@wayai/core/contracts';

const USAGE = `
wayai access explain — the access set a hub agent's toolset calls send, and why

Usage:
  wayai access explain --agent <agent_id> [--hub <uuid|folder>] [--user <user_id>] [--json]

Prints the read and write sets, per access dimension, that every hub toolset call of the
agent sends, the layers they are the intersection of, and, for each value, the layers that
removed it. Every layer only narrows:
  - with --user, or with no --user when you sign in as a person (yourself): that person's
    kind on the hub, the first that matches of org member, hub admin, hub team member,
    hub user and everyone else, then that kind's values ∩ the hub's ∩ the agent's. That
    is what their turn in their own conversation gets on a channel that verifies them (the
    app, WhatsApp, Instagram, Telegram); on any other they are everyone else.
  - with a scoped API token and no --user: the hub's ∩ the agent's, what a hub
    automation's turn of the agent gets.
Everyone else takes the hub's public values, customer-only: held only on record types that
customer scope narrows to one customer, on a call naming its customer. A turn in another
person's conversation is also cut to that person's set, a delegated task's to its
delegation's, and a background agent's side-call's to its host turn's agent's values: those
are not shown here. Explaining someone else matches no unredeemed team invite by their
email: once they redeem one, its values apply.

Nothing filters any record by these sets yet.

The hub is --hub, else this workspace's. --user for anyone but yourself needs an
organization admin.
`.trim();

/** How each layer reads in the printout. */
const LAYER_NAMES: Readonly<Record<AccessLayer, string>> = {
  hub: 'hub',
  org_member: 'org member',
  hub_admin: 'hub admin',
  hub_team_member: 'hub team member',
  hub_user: 'hub user',
  hub_public: 'hub public values',
  initiator_agent: 'consult initiator agent',
  delegation: 'delegation',
  originator: 'delegation originator',
  host_agent: 'host agent',
  agent: 'agent',
};

/** A kind reads as its layer, but everyone else. */
const kindName = (kind: NonNullable<AccessExplain['kind']>): string => (kind === 'public' ? 'everyone else' : LAYER_NAMES[kind]);

function usageError(message: string): Error {
  return expected(`${message}\n\n${USAGE}`);
}

function parse(args: string[]) {
  try {
    return parseArgs({
      args,
      options: {
        hub: { type: 'string' },
        agent: { type: 'string' },
        user: { type: 'string' },
        json: { type: 'boolean' },
      },
      allowPositionals: true,
      strict: true,
    });
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
}

const text = (value: string): string => sanitizeTerminalText(value);
const list = (ids: readonly string[]): string => (ids.length === 0 ? 'nothing' : ids.map(text).join(' '));

/** A layer's values, one line per dimension. */
function layerLines(values: AccessAssignment | null): string[] {
  if (values === null) return ['narrows nothing (no values)'];
  const dimensions = Object.entries(values);
  if (dimensions.length === 0) return ['reaches nothing (unset, or no dimension)'];
  return dimensions.map(([dimension, { read, write }]) => `${text(dimension)}: read ${list(read)}; write ${list(write)}`);
}

/** The explanation as the lines `wayai access explain` prints. */
export function formatExplanation(data: AccessExplain): string[] {
  const lines: string[] = [];
  const who = data.user_id === null
    ? 'no person: the hub\'s ∩ the agent\'s'
    : `person ${text(data.user_id)}, ${data.kind ? kindName(data.kind) : 'unknown kind'}`;
  lines.push(`Hub ${text(data.hub_id)}, agent ${text(data.agent_id)}, ${who}`);
  if (data.values_hub_id === null) lines.push('This preview hub was never published: it has no hub values, so it reaches nothing.');
  else if (data.values_hub_id !== data.hub_id) lines.push(`Hub values are those of its production hub ${text(data.values_hub_id)}.`);
  if (data.customer_only) {
    lines.push('Customer-only: held only on record types customer scope narrows to one customer, on a call naming its customer; every other scoped type reaches nothing.');
  }
  if (data.conversations_reach_nothing) {
    lines.push('This person\'s own set reaches nothing, whatever the agent: every turn in their conversations, a team member\'s send or consult there included, reaches nothing either.');
  }

  lines.push('', 'Layers, intersected in order:');
  const width = Math.max(...data.layers.map((layer) => LAYER_NAMES[layer.layer].length));
  for (const layer of data.layers) {
    const [first, ...rest] = layerLines(layer.values);
    lines.push(`  ${LAYER_NAMES[layer.layer].padEnd(width)}  ${first}`);
    for (const line of rest) lines.push(`  ${''.padEnd(width)}  ${line}`);
  }

  lines.push('', 'Effective set:');
  if (data.dimensions.length === 0) lines.push('  no dimension: the set reaches nothing on any scoped record type');
  for (const dimension of data.dimensions) {
    lines.push(`  ${text(dimension.dimension_id)}: read ${list(dimension.read)}; write ${list(dimension.write)}`);
    for (const removal of dimension.read_removed) {
      lines.push(`    ${text(removal.value_id)}  not read: removed by ${removal.layers.map((layer) => LAYER_NAMES[layer]).join(', ')}`);
    }
    for (const removal of dimension.write_removed) {
      lines.push(`    ${text(removal.value_id)}  read, not written: removed by ${removal.layers.map((layer) => LAYER_NAMES[layer]).join(', ')}`);
    }
  }
  lines.push('', 'Nothing filters any record by this set yet.');
  return lines;
}

function failure(err: unknown): unknown {
  if (err instanceof ApiError && err.isExpected) return expected(sanitizeTerminalText(extractApiMessage(err)));
  return err;
}

export async function accessCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const { values, positionals } = parse(args);
  const [sub, ...rest] = positionals;
  if (sub !== 'explain') throw usageError(`Unknown subcommand ${JSON.stringify(sub)}.`);
  if (rest.length > 0) throw usageError('explain takes no arguments.');
  if (values.agent === undefined) throw usageError('explain needs --agent <agent_id>.');
  if (!UUID_RE.test(values.agent)) throw expected(`Invalid --agent ${JSON.stringify(values.agent)}: expected an agent id (a UUID).`);
  if (values.user !== undefined && !USER_ID_REGEX.test(values.user)) {
    throw expected(`Invalid --user ${JSON.stringify(values.user)}: expected a user id.`);
  }
  const hubId = resolveActiveHubId(values.hub === undefined ? [] : ['--hub', values.hub]);

  const { config, accessToken } = await requireAuth();
  // A browser login is a JWT that expires; refresh it on a 401 instead of failing.
  const api = new ApiClient({ apiUrl: config.api_url, accessToken, onUnauthorized: () => getAccessToken(config) });
  let data: AccessExplain;
  try {
    ({ data } = await api.explainAccess(hubId, values.agent, values.user));
  } catch (err) {
    throw failure(err);
  }
  console.log(values.json ? JSON.stringify(data, null, 2) : formatExplanation(data).join('\n'));
}
